import {hideOthers} from 'aria-hidden';
import {useCallback, useEffect, useRef} from 'react';
import {FOCUS_SELECTOR, focusable, tabbable} from './tabbable';
import {scopeTab} from './scopeTab';

/**
 * A custom hook to hide all other elements in the browser from screen-readers
 * when the Modal is open.
 *
 * The aria-hidden implementation is powered by the aria-hidden library.
 *
 * @see https://github.com/webflow/webflow/pull/46385#issuecomment-1091838478
 * @see https://github.com/theKashey/aria-hidden
 */
const useAriaHideOthers = ({
  active,
  portalRef,
}: {
  active: boolean;
  portalRef: React.RefObject<HTMLElement>;
}) => {
  const portalNode = portalRef.current;

  useEffect(
    () => {
      if (!portalNode) return undefined;
      return hideOthers(portalNode);
    },
    /**
     * active is required as a dependency as this effect reacts to the
     * change in the active state to hide or show the other elements.
     */
    [portalNode, active]
  );
};

/**
 * Enhancement for the FocusTrap component.
 *
 * We only want to automatically focus the first focusable element if the
 * current focused element is not contained within the dialog-like UI.
 *
 * This is to account for (intentional) focus jumping to specific controls,
 * such as an input field.
 *
 * 2023-03-09
 */
const handleOnFocusElement = (
  refElement: HTMLElement | null,
  element: HTMLElement | null
) => {
  /**
   * Arbitrary time to wait for potential autofocus to occur.
   * 16ms = 1 frame at 60fps.
   */
  const focusTimeAwaitingPotentialAutofocus = 16;
  /**
   * Delay the focus by a frame, allowing for some (arbitrary) time for
   * potential focus jumps (autofocus) to occur.
   */
  setTimeout(() => {
    const currentFocusedElement = document.activeElement;
    const isFocusedElementWithin = refElement?.contains?.(
      currentFocusedElement
    );
    if (isFocusedElementWithin) return;

    element?.focus?.();
  }, focusTimeAwaitingPotentialAutofocus);
};

/**
 * Custom hook to contain keyboard triggered focus handling (Tab or Shift+Tab)
 * with an HTMLElement dialog-like UI (e.g. Modal or Popovers).
 *
 * Implementation is modified from the useFocusTrap hook from Mantine.
 * @see https://github.com/mantinedev/mantine/blob/master/src/mantine-hooks/src/use-focus-trap/use-focus-trap.ts
 */
export function useFocusTrap(
  active: boolean = true
): (instance: HTMLElement | null) => void {
  const ref = useRef<HTMLElement | null>(null);
  useAriaHideOthers({active, portalRef: ref});

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      if (!active) {
        return;
      }

      if (ref.current) return;

      if (node) {
        const processNode = (_node: HTMLElement) => {
          let focusElement: HTMLElement | null = null;

          const children = Array.from<HTMLElement>(
            node.querySelectorAll(FOCUS_SELECTOR)
          );
          focusElement =
            children.find(tabbable) || children.find(focusable) || null;
          if (!focusElement && focusable(node)) focusElement = node;

          if (focusElement) {
            handleOnFocusElement(ref.current, focusElement);
          } else if (process.env.NODE_ENV === 'development') {
            console.warn(
              'Failed to find focusable element within provided node',
              node
            );
          }
        };

        // Delay processing the HTML node by a frame. This ensures focus is assigned correctly.
        setTimeout(() => {
          if (node.ownerDocument) {
            processNode(node);
          } else if (process.env.NODE_ENV === 'development') {
            console.warn('Ref node is not part of the dom', node);
          }
        });

        ref.current = node;
      } else {
        ref.current = null;
      }
    },
    [active]
  );

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && ref.current) {
        scopeTab(ref.current, event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active]);

  return setRef;
}
